import {Capacitor, CapacitorHttp} from '@capacitor/core'
import {Directory, Filesystem} from '@capacitor/filesystem'
import {FileOpener} from '@capacitor-community/file-opener'
import {AuthenticationService} from '@/service/authentication/AuthenticationService'
import {Router} from 'vue-router'

const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv: 'text/csv',
  txt: 'text/plain',
  zip: 'application/zip',
}

export class PwaMessageHandler {
  
  private readonly allowedOrigins: string[]
  
  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router
  ) {
    this.allowedOrigins = this.buildAllowedOrigins()
    console.log('Origines PWA autorisées:', this.allowedOrigins)
  }
  
  register(): void {
    window.addEventListener('message', (event) => this.handleMessage(event))
  }
  
  private buildAllowedOrigins(): string[] {
    const pwaUrl = process.env.VUE_APP_PWA_URL
    const origins: string[] = [
      'http://localhost:8000',
      'https://localhost:8000',
      'http://127.0.0.1:8000',
      'https://127.0.0.1:8000',
      'https://dev-2ndevie.rockease.fr/',
      'https://rec-2ndevie.rockease.fr/',
      'https://prd-2ndevie.rockease.fr/',
      'https://2ndevie.rockease.fr/',
      'https://dev-poc-2ndvie.csp-digital.com',
      'https://dev-pwa-yosemite.csp-digital.com',
    ]
    
    if (pwaUrl) {
      try {
        const url = new URL(pwaUrl)
        const origin = url.origin
        if (!origins.includes(origin)) {
          origins.push(origin)
          console.log('Origine PWA ajoutée depuis env:', origin)
        }
      } catch (error) {
        console.warn('URL PWA invalide:', pwaUrl, error)
      }
    }
    
    return origins
  }
  
  private async handleMessage(event: MessageEvent): Promise<void> {
    if (!this.allowedOrigins.includes(event.origin)) {
      console.warn('Message reçu depuis une origine non autorisée:', event.origin)
      return
    }
    
    switch (event.data?.type) {
    case 'CLOSE_SECONDE_VIE':
      this.handleCloseSecondeVie()
      break
    case 'REQUEST_TOKEN_REFRESH':
      await this.handleTokenRefresh(event)
      break
    case 'DOWNLOAD_REQUEST':
      await this.handleDownload(event)
      break
    case 'SHARE_VIA':
      this.handleShareVia(event)
      break
    case 'PHONE_CALL':
      this.handlePhoneCall(event)
      break
    }
  }
  
  private handleCloseSecondeVie(): void {
    this.router.push('/').catch(e => console.error('Erreur lors de la redirection après réception du message de fermeture :', e))
  }
  
  private async handleTokenRefresh(event: MessageEvent): Promise<void> {
    try {
      const refreshedToken = await this.authenticationService.refreshToken()
      
      if (event.source) {
        (event.source as Window).postMessage({
          type: 'TOKEN_REFRESH_RESPONSE',
          token: refreshedToken.token,
          expiresOn: refreshedToken.expiresOn,
          success: true
        }, event.origin)
      }
    } catch (error) {
      console.error('Erreur lors du rafraîchissement du token:', error)
      
      if (event.source) {
        (event.source as Window).postMessage({
          type: 'TOKEN_REFRESH_RESPONSE',
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }, event.origin)
      }
    }
  }
  
  private async handleDownload(event: MessageEvent): Promise<void> {
    try {
      const {url, filename} = event.data
      if (!url || !filename) {
        return
      }
      
      const authorization = await this.authenticationService.getAuthorization()
      
      const extMatch = filename.match(/\.([a-zA-Z0-9]+)$/)
      const ext = extMatch ? extMatch[1].toLowerCase() : ''
      const contentType = MIME_TYPES[ext] || 'application/octet-stream'
      
      const dirPath = 'exported/2ndevie'
      const filePath = `${dirPath}/${filename}`
      
      try {
        await Filesystem.mkdir({
          path: dirPath,
          directory: Directory.Cache,
          recursive: true
        })
      } catch (_e) {
        // Ignorer si le répertoire existe déjà
      }
      
      const httpResponse = await CapacitorHttp.get({
        url: url,
        headers: {'Authorization': `Bearer ${authorization}`},
        responseType: 'blob'
      })
      
      if (httpResponse.status < 200 || httpResponse.status >= 300) {
        return
      }
      
      await Filesystem.writeFile({
        path: filePath,
        data: httpResponse.data,
        directory: Directory.Cache
      })
      
      const statResult = await Filesystem.stat({path: filePath, directory: Directory.Cache})
      const platform = Capacitor.getPlatform()
      
      if (platform === 'android') {
        await FileOpener.open({filePath: statResult.uri, contentType})
      } else if (platform === 'ios') {
        const readResult = await Filesystem.readFile({path: filePath, directory: Directory.Cache})
        const writeResult = await Filesystem.writeFile({
          path: filename,
          data: readResult.data,
          directory: Directory.External
        })
        await FileOpener.open({filePath: writeResult.uri, contentType})
      }
    } catch (error) {
      console.error('Erreur lors du traitement de la demande de téléchargement :', error)
    }
  }
  
  private handleShareVia(event: MessageEvent): void {
    try {
      const {channel, text} = event.data
      const prefix = '📢 Annonce publiée sur Rockease - Bourse aux Matériaux'
      const encodedText = encodeURIComponent(text || '')
      const platform = Capacitor.getPlatform()
      
      switch (channel) {
      case 'sms': {
        const smsText = encodeURIComponent(`${prefix}\n${text || ''}`)
        const smsUrl = platform === 'ios'
          ? `sms:&body=${smsText}`
          : `sms:?body=${smsText}`
        window.open(smsUrl, '_system')
        break
      }
      case 'whatsapp': {
        const whatsappText = encodeURIComponent(`${prefix}\n${text || ''}`)
        window.open(`https://wa.me/?text=${whatsappText}`, '_system')
        break
      }
      case 'email': {
        const encodedSubject = encodeURIComponent(prefix)
        window.open(`mailto:?subject=${encodedSubject}&body=${encodedText}`, '_system')
        break
      }
      case 'copy':
        break
      default:
        console.warn('Canal de partage non supporté:', channel)
      }
    } catch (error) {
      console.error('Erreur lors du traitement de la demande de partage :', error)
    }
  }
  
  private handlePhoneCall(event: MessageEvent): void {
    window.open(`tel:${event.data.phoneNumber}`, '_system')
  }
}
